import { useMemo } from "react";
import { SearchableSelectOption } from "../../../Components/Common/SearchableSelect";
import { useCommunesQuery, usePrioritesQuery, useStatutAlertesQuery } from "../../../hooks/queries/useRoadAlertQueries";

interface FilterOptions {
  communeOptions: SearchableSelectOption[];
  prioriteOptions: SearchableSelectOption[];
  statutOptions: SearchableSelectOption[];
}

/**
 * Hook pour les données de filtres (communes, priorités, statuts) via TanStack Query
 */
export const useFilterData = () => {
  const communesQuery = useCommunesQuery();
  const prioritesQuery = usePrioritesQuery();
  const statutsQuery = useStatutAlertesQuery();

  const communes = useMemo(() => communesQuery.data ?? [], [communesQuery.data]);
  const priorites = useMemo(() => prioritesQuery.data ?? [], [prioritesQuery.data]);
  const statuts = useMemo(() => statutsQuery.data ?? [], [statutsQuery.data]);
  const loading = communesQuery.isLoading || prioritesQuery.isLoading || statutsQuery.isLoading;
  const error = communesQuery.error ?? prioritesQuery.error ?? statutsQuery.error;

  const options: FilterOptions = useMemo(() => {
    const communesMap = new Map<string, SearchableSelectOption>();
    communesMap.set("", { value: "", label: "--Tous--" });
    communes.forEach((c: any) => {
      const value = String(c.idCommune || c.id);
      if (!communesMap.has(value)) {
        communesMap.set(value, {
          value: value,
          label: c.libelleCommune || c.libelle || "Commune sans nom",
        });
      }
    });

    const prioritesMap = new Map<string, SearchableSelectOption>();
    prioritesMap.set("", { value: "", label: "--Tous--" });
    if (Array.isArray(priorites)) {
      priorites.forEach((p: any) => {
        const value = String(p.id || p.idPriorite || p.value);
        const label = p.libellePriorite || p.libelle || p.label || `Priorité ${value}`;
        if (value && value !== "" && !prioritesMap.has(value)) {
          prioritesMap.set(value, { value: value, label: label });
        }
      });
    }

    const statutsMap = new Map<string, SearchableSelectOption>();
    statutsMap.set("", { value: "", label: "--Tous--" });
    if (Array.isArray(statuts)) {
      statuts.forEach((s: any) => {
        const value = String(s.id || s.idStatutAlerte || s.value);
        const label = s.libelleStatutAlerte || s.libelle || s.label || `Statut ${value}`;
        if (value && value !== "" && !statutsMap.has(value)) {
          statutsMap.set(value, { value: value, label: label });
        }
      });
    }

    return {
      communeOptions: Array.from(communesMap.values()),
      prioriteOptions: Array.from(prioritesMap.values()),
      statutOptions: Array.from(statutsMap.values()),
    };
  }, [communes, priorites, statuts]);

  return {
    communes,
    priorites,
    statuts,
    options,
    loading,
    error: error ? String(error) : null,
  };
};
